import {
  inject,
  Input,
  Output,
  ViewChild,
  Component,
  DestroyRef,
  ElementRef,
  EventEmitter,
  HostListener,
  ChangeDetectionStrategy,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

import { Store } from '@ngrx/store';

import { IconProp } from '@fortawesome/fontawesome-svg-core';

import { SidebarService } from '../../services/sidebar.service';

import { AppFeatures } from '../../app.features';
import { DataObject, ObjectPermission } from '../../models/core.models';

import { AppState } from 'src/app/reducers';
import { selectCoreLanguage } from '../../reducers';

import { tap } from 'rxjs';
import { NomLanguages } from '../../constants';

// sidebar-config.ts
export enum SidebarButtonBehavior {
  COMPONENT = 'component',
  ACTION = 'action',
}

export interface SidebarButtonConfig {
  key: string;
  label: string;
  componentName?: string;
  header: string;
  icon?: IconProp;
  shouldDisplay?: boolean;
  permission?: AppFeatures; // optional permission to check
  objectPermission?: ObjectPermission[];
  counter?: number;
  inputs?: {
    [key: string]: any;
  };
  behaviorType?: SidebarButtonBehavior;
  actionHandler?: () => void;
}

@Component({
  selector: 'app-sidebar',
  templateUrl: './sidebar.component.html',
  styleUrls: ['./sidebar.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class SidebarComponent {
  @Input() buttons: SidebarButtonConfig[] = [];
  @Input() object: DataObject;
  @Output() componentSelected = new EventEmitter<SidebarButtonConfig>();
  @ViewChild('sidebarPanel') sidebarPanel: ElementRef;

  isOpen: boolean;
  selectedComponent: string;
  selectedComponentTitle: string;
  selectedComponentIcon: IconProp;
  isRtl: boolean;

  private destroyRef = inject(DestroyRef);

  constructor(
    public sidebarService: SidebarService,
    private store: Store<AppState>,
  ) {
    this.sidebarService.isOpen$
      .pipe(
        tap((isOpen) => (this.isOpen = isOpen)),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe();

    this.sidebarService.selectedComponent$
      .pipe(
        tap((component) => (this.selectedComponent = component)),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe();

    this.sidebarService.selectedComponentTitle$
      .pipe(
        tap((title) => (this.selectedComponentTitle = title)),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe();

    this.sidebarService.selectedComponentIcon$
      .pipe(
        tap((icon) => (this.selectedComponentIcon = icon)),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe();

    this.sidebarService.componentSelected
      .pipe(
        tap((componentName) => this.componentSelected.emit(componentName)),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe();

    this.store
      .select(selectCoreLanguage)
      .pipe(
        tap((language) => {
          this.isRtl = language.id === NomLanguages.ARABIC;
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe();
  }

  @HostListener('document:click', ['$event'])
  clickOutside(event: MouseEvent) {
    // if (!this.sidebarPanel.nativeElement.contains(event.target)) {
    //   this.sidebarService.closeSidebar();
    // }
  }

  selectComponent(button: SidebarButtonConfig) {
    const behaviorType = button.behaviorType || SidebarButtonBehavior.COMPONENT;

    if (behaviorType === SidebarButtonBehavior.ACTION) {
      if (button.actionHandler) {
        button.actionHandler();
      } else {
        console.warn('Action button missing actionHandler:', button.key);
      }
    } else {
      if (button.componentName) {
        this.sidebarService.selectComponent(button);
      } else {
        console.warn('Component button missing componentName:', button.key);
      }
    }
  }
}
